import { Search } from '@mui/icons-material';
import {
    Autocomplete,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    type InputProps,
    Radio,
    Skeleton,
    Stack,
    type SxProps,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { _ } from '/@/locale';

interface SelectItem {
    id: number;
    label: string;
    disabled?: string;
    /** Marks the synthetic "typed text, no match" row injected by onAddOther — rendered without a checkbox/radio. */
    isOtros?: boolean;
    /** Optional trailing content (e.g. a role chip) rendered to the right of the label. */
    chip?: ReactNode;
}

/** Sentinel id for the synthetic "Otros" row; real items always have a positive backend id. */
const OTROS_OPTION_ID = -1;

type SelectCheckboxSingleProps = {
    multiple?: false;
    selectedItem: number | null;
    setSelectedItem: Dispatch<SetStateAction<number | null>>;
};

type SelectCheckboxMultipleProps = {
    multiple: true;
    /** Ids currently checked; these render with a checked checkbox even while the list stays unfiltered. */
    selectedItems: number[];
    /** Called with the option the user just checked/unchecked; the caller decides whether that adds or removes it. */
    onToggle: (item: SelectItem) => void;
};

type SelectCheckboxProps = (SelectCheckboxSingleProps | SelectCheckboxMultipleProps) & {
    InputProps?: InputProps;
    items: SelectItem[];
    verbose?: string;
    loading?: boolean;
    sx?: SxProps<Theme>;
    /** Keep the results list expanded permanently instead of the normal open-on-focus dropdown. Default true. */
    alwaysOpen?: boolean;
    /** When set, a search with zero matches offers to add the typed text as a free-text ("Otros") entry instead of just showing "no results". */
    onAddOther?: (query: string) => void;
    /** Called once each time a search goes from having matches to having none. The text is not passed. */
    onNoResults?: () => void;
};

const SelectCheckbox = (props: SelectCheckboxProps) => {
    const {
        InputProps,
        items,
        verbose: _verbose,
        loading = false,
        sx,
        alwaysOpen = true,
        onAddOther,
        onNoResults,
    } = props;
    const isMultiple = props.multiple === true;
    const selectedIds = isMultiple ? props.selectedItems : props.selectedItem !== null ? [props.selectedItem] : [];
    // Keyed on the ids themselves (not array identity) so this stays referentially stable across
    // re-renders that don't actually change the selection — MUI's Autocomplete resets its input
    // text whenever the `value` array it's given changes reference, which a fresh `.filter()`
    // result on every render would otherwise trigger on every keystroke.
    const selectedIdsKey = isMultiple ? props.selectedItems.join(',') : String(props.selectedItem);
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the stable selectedIdsKey primitive, not selectedIds itself (a fresh array every render)
    const selectedOptions = useMemo(
        () => items.filter((item) => selectedIds.includes(item.id)),
        [items, selectedIdsKey]
    );
    const [inputValue, setInputValue] = useState('');

    // Same match as `filterOptions`, so "no results" means what the user sees
    const searchTerm = inputValue.toLowerCase().trim();
    const hasNoResults = Boolean(searchTerm) && !items.some((item) => item.label.toLowerCase().includes(searchTerm));
    const reportedNoResults = useRef(false);
    // biome-ignore lint/correctness/useExhaustiveDependencies: reported on the change, not on every new callback
    useEffect(() => {
        if (hasNoResults && !reportedNoResults.current) onNoResults?.();
        reportedNoResults.current = hasNoResults;
    }, [hasNoResults]);

    return (
        <Stack spacing={3} sx={{ width: '100%', pb: '300px', ...sx }}>
            {loading ? (
                <Stack spacing={1}>
                    <Skeleton variant='rounded' height={56} />
                    <Skeleton variant='text' height={40} />
                    <Skeleton variant='text' height={40} />
                    <Skeleton variant='text' height={40} />
                </Stack>
            ) : (
                <Autocomplete
                    multiple={isMultiple}
                    options={items}
                    value={isMultiple ? selectedOptions : (selectedOptions[0] ?? null)}
                    onChange={(_event, newValue, reason, details) => {
                        if (isMultiple) {
                            if (reason === 'clear') {
                                for (const option of selectedOptions) props.onToggle(option);
                                return;
                            }
                            if (details?.option?.isOtros) {
                                onAddOther?.(details.option.label);
                                setInputValue('');
                                return;
                            }
                            if (details?.option) props.onToggle(details.option);
                            return;
                        }
                        const option = newValue as SelectItem | null;
                        if (option?.isOtros) {
                            onAddOther?.(option.label);
                            setInputValue('');
                            return;
                        }
                        props.setSelectedItem(option?.id ?? null);
                    }}
                    getOptionLabel={(option) => option.label}
                    getOptionDisabled={(option) => !!option.disabled}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, { inputValue }) => {
                        const searchTerm = inputValue.toLowerCase().trim();
                        if (!searchTerm) return options;
                        const filtered = options.filter((option) => option.label.toLowerCase().includes(searchTerm));
                        if (onAddOther && filtered.length === 0) {
                            return [{ id: OTROS_OPTION_ID, label: inputValue.trim(), isOtros: true }];
                        }
                        return filtered;
                    }}
                    {...(alwaysOpen ? { open: true } : {})}
                    disableCloseOnSelect
                    disableClearable={!isMultiple}
                    renderTags={() => null}
                    inputValue={inputValue}
                    onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
                    noOptionsText={_('No se encontraron resultados. Revisa el nombre o apellido ingresado.')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={InputProps?.placeholder || _('Buscar...')}
                            color='secondary'
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        if (option.isOtros) {
                            return (
                                <li {...props} key='otros-option'>
                                    <Typography variant='body1' color='primary' sx={{ py: 0.5, px: 1 }}>
                                        {option.label}
                                    </Typography>
                                </li>
                            );
                        }
                        const isSelected = selectedIds.includes(option.id);
                        const isDisabled = !!option.disabled;
                        return (
                            <li {...props} key={option.id}>
                                <FormControlLabel
                                    control={
                                        isMultiple ? (
                                            <Checkbox
                                                checked={isSelected}
                                                color='secondary'
                                                sx={{ p: 0.5 }}
                                                disabled={isDisabled}
                                            />
                                        ) : (
                                            <Radio
                                                checked={isSelected}
                                                color='secondary'
                                                sx={{ p: 0.5 }}
                                                disabled={isDisabled}
                                            />
                                        )
                                    }
                                    label={
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            justifyContent='space-between'
                                            spacing={1}
                                            sx={{ ml: 1, width: '100%' }}
                                        >
                                            <Typography variant='body1' color={'primary'}>
                                                {option.label}
                                            </Typography>
                                            {option.chip}
                                        </Stack>
                                    }
                                    sx={{ m: 0, width: '100%', '& .MuiFormControlLabel-label': { width: '100%' } }}
                                />
                            </li>
                        );
                    }}
                    ListboxProps={{
                        sx: {
                            maxHeight: 300,
                            '& .MuiAutocomplete-option': {
                                py: 1,
                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                '&:last-child': {
                                    borderBottom: 'none',
                                },
                            },
                        },
                    }}
                    sx={{ width: '100%' }}
                />
            )}
        </Stack>
    );
};

export default SelectCheckbox;
